import { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Box,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { get, post } from "src/utils/networkUtils";

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString();
}

function CompetitionRow({ competition, onAdd, addingTcId }) {
  const [open, setOpen] = useState(false);
  const calendars = competition.tournamentCalendars ?? [];

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{competition.name ?? "-"}</TableCell>
        <TableCell>{competition.knownName ?? "-"}</TableCell>
        <TableCell>{competition.country ?? "-"}</TableCell>
        <TableCell>{competition.competitionFormat ?? "-"}</TableCell>
        <TableCell>{competition.type ?? "-"}</TableCell>
        <TableCell align="center">
          <Chip label={calendars.length} size="small" variant="outlined" />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom component="div">
                Tournament Calendars
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calendars.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No tournament calendars found.
                      </TableCell>
                    </TableRow>
                  )}
                  {calendars.map((tc) => (
                    <TableRow key={tc.id} hover>
                      <TableCell>{tc.name ?? "-"}</TableCell>
                      <TableCell>{formatDate(tc.startDate)}</TableCell>
                      <TableCell>{formatDate(tc.endDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={tc.active ? "Yes" : "No"}
                          size="small"
                          color={tc.active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        {tc.alreadyImported ? (
                          <Chip label="Imported" color="success" size="small" />
                        ) : (
                          <Chip
                            label="Not imported"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!tc.alreadyImported && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={addingTcId === tc.id}
                            onClick={() => onAdd(competition, tc)}
                          >
                            {addingTcId === tc.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              "Add"
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function OptaImportTournamentsPage() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addingTcId, setAddingTcId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchCompetitions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await get("/admin/opta/competitions");
      setCompetitions(r?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to fetch competitions"
      );
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompetitions();
  }, [fetchCompetitions]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return competitions;
    return competitions.filter((c) => {
      const haystack = [
        c.name,
        c.knownName,
        c.country,
        c.competitionCode,
        c.type,
        c.competitionType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [competitions, searchQuery]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAdd = async (competition, tc) => {
    setAddingTcId(tc.id);
    try {
      const metadata = {
        competition: {
          id: competition.id,
          ocId: competition.ocId,
          opId: competition.opId,
          knownName: competition.knownName,
          displayOrder: competition.displayOrder,
          country: competition.country,
          countryId: competition.countryId,
          isFriendly: competition.isFriendly,
          type: competition.type,
          competitionType: competition.competitionType,
          rawCompetitionFormat: competition.competitionFormat,
        },
        tournamentCalendar: {
          includesVenues: tc.includesVenues,
          ocId: tc.ocId,
          lastUpdated: tc.lastUpdated,
          includesStandings: tc.includesStandings,
        },
      };

      await post("/admin/opta/tournament-calendars", {
        optaTournamentCalendarId: tc.id,
        competitionName: competition.name ?? tc.name ?? "",
        seasonLabel: tc.name ?? "",
        startDate: tc.startDate ?? null,
        endDate: tc.endDate ?? null,
        shortCode: competition.competitionCode ?? null,
        competitionFormat: competition.competitionFormat ?? null,
        metadata,
      });

      // Optimistically update local state
      setCompetitions((prev) =>
        prev.map((c) => {
          if (c.id !== competition.id) return c;
          return {
            ...c,
            tournamentCalendars: (c.tournamentCalendars ?? []).map((t) =>
              t.id === tc.id ? { ...t, alreadyImported: true } : t
            ),
          };
        })
      );

      setSnackbar({
        open: true,
        message: "Tournament added successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ??
          err?.message ??
          "Failed to add tournament",
        severity: "error",
      });
    } finally {
      setAddingTcId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  return (
    <>
      <Helmet>
        <title>
          OPTA Tournament Import | EngageCraft Fantasy Football Admin
        </title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          OPTA Tournament Import
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Browse OPTA competitions and their tournament calendars. Expand a
          competition to see its calendars. Click <strong>Add</strong> to import
          a missing calendar into the local database.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Search by name, known name, country, format, or type..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Name</TableCell>
                  <TableCell>Known Name</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Calendars</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      No competitions found.
                    </TableCell>
                  </TableRow>
                )}
                {paginated.map((competition) => (
                  <CompetitionRow
                    key={competition.id}
                    competition={competition}
                    onAdd={handleAdd}
                    addingTcId={addingTcId}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Card>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
